import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faUser,
  faSignOutAlt,
  faCaretDown,
  faFlower,
  faSeedling,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

const Header = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { currentUser, logout, isAdmin } = useAuth();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setUserMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-white/95 shadow-pastel-sm backdrop-blur-sm transition-all duration-300 before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-0.5 before:bg-gradient-to-r before:from-blush before:via-secondary before:to-sky before:opacity-60">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="group flex items-center">
              <img src={logo} alt="Curators' Studio" className="h-12" />
            </Link>
          </div>

          {/* Navigation removed as per requirement - moved to footer */}

          {/* Action Buttons */}
          <div
            className={`flex items-center ${
              !currentUser && !isOpen ? 'md:flex' : 'flex'
            }`}
          >
            {currentUser ? (
              <div className="flex items-center">
                <button
                  onClick={() => {
                    // Check current location
                    const isHomePage = window.location.pathname === '/';

                    if (!isHomePage) {
                      // Navigate to homepage first if we're not already there
                      navigate('/');

                      // Scroll after navigation and component mounting
                      setTimeout(() => {
                        const featuredSection =
                          document.getElementById('featured-flowers');
                        if (featuredSection) {
                          featuredSection.scrollIntoView({
                            behavior: 'smooth',
                          });
                        }
                      }, 300);
                    } else {
                      // Already on homepage, just scroll
                      const featuredSection =
                        document.getElementById('featured-flowers');
                      if (featuredSection) {
                        featuredSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                  className="inline-block px-7 py-3 font-semibold uppercase tracking-wider text-sm border border-secondary/60 text-primary hover:bg-gradient-to-r hover:from-secondary/80 hover:to-peach/80 hover:text-dark transition-all duration-300 hover:shadow-pastel-md rounded-soft overflow-hidden relative transform hover:-translate-y-0.5"
                >
                  <span className="relative z-10">Subscribe</span>
                </button>

                {/* User Menu */}
                <div className="relative ml-4" ref={userMenuRef}>
                  <button
                    className="flex items-center bg-transparent border-none font-medium uppercase tracking-wider text-sm text-primary hover:text-secondary transition-colors duration-200 px-2 py-1"
                    onClick={toggleUserMenu}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                    {currentUser.name && currentUser.name.split(' ')[0]}
                    <FontAwesomeIcon icon={faCaretDown} className="ml-1" />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-soft shadow-pastel-md z-50 before:content-[''] before:absolute before:-top-1.5 before:right-5 before:w-2.5 before:h-2.5 before:bg-white before:border-t before:border-l before:border-gray-100 before:transform before:rotate-45">
                      <Link
                        to="/profile"
                        className="block px-5 py-3 text-text-dark hover:bg-gray-50 hover:text-secondary transition-colors duration-200 first:rounded-t-soft"
                      >
                        <FontAwesomeIcon icon={faUser} className="w-4 mr-2.5" />
                        My Profile
                      </Link>

                      {isAdmin && (
                        <Link
                          to="/admin/flowers"
                          className="block px-5 py-3 text-text-dark hover:bg-gray-50 hover:text-secondary transition-colors duration-200"
                        >
                          <FontAwesomeIcon
                            icon={faSeedling}
                            className="w-4 mr-2.5"
                          />
                          Manage Flowers
                        </Link>
                      )}

                      <button
                        className="w-full text-left px-5 py-3 text-text-dark hover:bg-gray-50 hover:text-secondary transition-colors duration-200 last:rounded-b-soft"
                        onClick={handleLogout}
                      >
                        <FontAwesomeIcon
                          icon={faSignOutAlt}
                          className="w-4 mr-2.5"
                        />
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center">
                <button
                  onClick={() => setAuthModalOpen(true)}
                  className="flex items-center bg-transparent border-none font-medium uppercase tracking-wider text-sm text-primary hover:text-secondary transition-colors duration-200 px-2 py-1"
                >
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Account
                </button>
              </div>
            )}

            {/* Auth Modal */}
            <AuthModal
              isOpen={authModalOpen}
              onClose={() => setAuthModalOpen(false)}
            />
          </div>

          {/* Mobile Menu Toggle */}
          <div
            className="md:hidden block text-primary hover:text-secondary transition-colors duration-200 cursor-pointer"
            onClick={toggleMenu}
          >
            <FontAwesomeIcon icon={faBars} size="lg" />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
